<template>
    <div>
        <FriendTabs />

        <h1>Since Your Last Visit</h1>

        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>

        <PageLoadingError v-else-if="errorMessage"
                          :error="errorMessage"
                          :code="errorCode"></PageLoadingError>

        <template v-else>
            <template v-if="!hasChanges">
                <div class="alert alert-success">
                    <h3>Nobody has deleted you since you last checked!</h3>
                    <p>You must be a nice person.</p>
                    <p>
                        <a class="btn btn-info"
                           href="/sync">
                            <i class="glyphicon glyphicon-refresh"></i> Check Again
                        </a>
                    &nbsp;
                        <a class="nextButton btn btn-blue"
                           href="/friends/deleted-me"><i class="glyphicon glyphicon-chevron-right"></i> See Who Deleted You Previously</a>
                    </p>
                </div>
            </template>

            <template v-if="hasUnseenDeletedFriends">
                <h2>Missing Friends Since Your Last Visit</h2>
                <Friends :friends="unseenDeletedFriends" />
            </template>

<!--            <template v-if="hasUnseenDeactivatedFriends">-->
<!--                <h2>Friends That Have Deactivated Since Your Last Visit</h2>-->
<!--                <Friends :friends="unseenDeactivatedFriends" />-->
<!--            </template>-->

            <template v-if="hasNewFriends">
                <h2>New Friends Since Your Last Visit</h2>
                <Friends :friends="newFriends" />
            </template>
        </template>

        <div v-if="!loading"
             class="text-center mt-1">
            <a href="/sync"
               class="btn btn-info"><i class="glyphicon glyphicon-refresh"></i> Check Friends Again</a>
        </div>
    </div>
</template>

<script>
import Friends from '@/app/pages/friends/Friends';
import FriendTabs from '@/app/pages/friends/FriendTabs';
import PageLoadingError from '@/app/components/PageLoadingError';
import PageLoadingSpinner from '@/app/components/PageLoadingSpinner';
import { mapState } from 'vuex';
import { sendBackendRequest } from '@/app/lib/Messaging/requests';
import FriendStatus from 'wdm-common/Friends/FriendStatus';
import RequestType from 'wdm-common/Messaging/RequestType';

export default {
    components: {
        PageLoadingSpinner,
        PageLoadingError,
        Friends,
        FriendTabs,
    },

    data() {
        return {
            /**
             * @type {boolean}
             */
            loading: true,

            /**
             * @type {String|null}
             */
            errorMessage: null,

            /**
             * @type {String|null}
             */
            errorCode: null,
        };
    },

    computed: {
        ...mapState(['whatsNew']),

        unseenDeletedFriends() {
            return this.whatsNew ? this.whatsNew.unseenDeletedFriends : null;
        },

        hasUnseenDeletedFriends() {
            return this.unseenDeletedFriends && this.unseenDeletedFriends.length > 0;
        },

        unseenDeactivatedFriends() {
            return this.whatsNew ? this.whatsNew.unseenDeactivatedFriends : null;
        },

        hasUnseenDeactivatedFriends() {
            return this.unseenDeactivatedFriends && this.unseenDeactivatedFriends.length > 0;
        },

        newFriends() {
            return this.whatsNew ? this.whatsNew.newFriends : null;
        },

        hasNewFriends() {
            return this.newFriends && this.newFriends.length > 0;
        },

        hasChanges() {
            return this.hasUnseenDeletedFriends
                || this.hasUnseenDeactivatedFriends
                || this.hasNewFriends;
        },
    },

    async created() {
        if (this.$store.state.whatsNew === null) {
            const whatsNew = {};
            await Promise.all([
                sendBackendRequest(
                    RequestType.GET_FRIENDS,
                    {
                        status: FriendStatus.DELETED__UNSEEN,
                    }
                )
                    .then(({ friends }) => {
                        whatsNew.unseenDeletedFriends = Object.values(friends);
                    })
                    .catch((e) => {
                        console.error(e);
                        this.errorMessage = e.errorMessage || e.message;
                        this.errorCode = e.errorCode;
                    }),

                sendBackendRequest(
                    RequestType.GET_FRIENDS,
                    {
                        status: FriendStatus.DEACTIVATED__UNSEEN,
                    }
                )
                    .then(({ friends }) => {
                        whatsNew.unseenDeactivatedFriends = Object.values(friends);
                    })
                    .catch((e) => {
                        console.error(e);
                        this.errorMessage = e.errorMessage || e.message;
                        this.errorCode = e.errorCode;
                    }),

                sendBackendRequest(
                    RequestType.GET_FRIENDS,
                    {
                        status: FriendStatus.NEW,
                    }
                )
                    .then(({ friends }) => {
                        whatsNew.newFriends = Object.values(friends);
                    })
                    .catch((e) => {
                        console.error(e);
                        this.errorMessage = e.errorMessage || e.message;
                        this.errorCode = e.errorCode;
                    }),
            ]);
            try {
                // TODO: Clear whatsnew on sync.
                // FIXME: whatsnew isn't persisted because we're not an SPA.
                this.$store.commit('setWhatsNew', whatsNew);

                if (this.hasChanges) {
                    this.setFriendsSeen();
                }
            } catch (e) {
                this.errorMessage = e.errorMessage || e.message;
                this.errorCode = e.errorCode;
            }

            this.loading = false;
        }
    },

    methods: {
        setFriendsSeen() {
            const friendIds = [
                ...this.unseenDeactivatedFriends ? this.unseenDeactivatedFriends.map((f) => f.friendId) : [],
                ...this.unseenDeletedFriends ? this.unseenDeletedFriends.map((f) => f.friendId) : [],
                ...this.newFriends ? this.newFriends.map((f) => f.friendId) : [],
            ];

            if (friendIds.length < 1) {
                return;
            }

            sendBackendRequest(
                RequestType.SET_FRIENDS_SEEN,
                {
                    friendIds,
                }
            )
                .then(() => {})
                .catch((e) => console.error(e));
        },
    },
};
</script>
